import { useEffect, useMemo } from "react";
import { View } from "react-native";
import { CommonActions } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import type { CareStackParamList } from "../types";
import { useDocumentUploadController } from "../DocumentUploadController";
import MetricDetailSheet from "../components/MetricDetailSheet";
import { useSmartBack } from "../hooks/useSmartBack";
import { getMetricsByKey } from "../data/metricFactsheets";
import {
  getDataPointForMarkerName,
  getDataPointsForTest,
  getTestsForDataPoint,
} from "@src/packages/content";
import { useLocale } from "@src/packages/i18n";

type Props = NativeStackScreenProps<CareStackParamList, "MetricDetail">;

export default function MetricDetailPreviewScreen({
  navigation,
  route,
}: Props) {
  const { openDocumentUpload } = useDocumentUploadController();
  const { locale } = useLocale();
  const handleBack = useSmartBack({
    fallbackTab: "Care",
    fallbackScreen: "CareOverview",
  });
  const metric = useMemo(() => {
    const byKey = getMetricsByKey(locale);
    const key = route.params?.metricKey ?? "estradiol";
    return byKey[key] ?? byKey.estradiol ?? null;
  }, [route.params?.metricKey, locale]);
  const canonicalTestId = useMemo(() => {
    if (!metric) return null;
    const dataPoint = getDataPointForMarkerName(metric.label);
    if (!dataPoint) return null;
    const tests = getTestsForDataPoint(dataPoint.id);
    return (
      tests.find((test) => getDataPointsForTest(test.id).length === 1)?.id ??
      tests[0]?.id ??
      null
    );
  }, [metric]);

  useEffect(() => {
    if (!canonicalTestId) return;
    const localState = navigation.getState();
    const hasOriginRoute = localState.index > 0;
    if (hasOriginRoute) {
      navigation.goBack();
    }
    navigation.getParent()?.dispatch(
      CommonActions.navigate({
        name: "Access",
        params: {
          screen: "TestDetail",
          params: {
            testId: String(canonicalTestId),
            ...(hasOriginRoute ? { returnTab: "Care" as const } : {}),
          },
        },
      }),
    );
  }, [canonicalTestId, navigation]);

  if (canonicalTestId) return null;

  return (
    <View style={{ flex: 1 }}>
      <MetricDetailSheet
        metric={metric}
        onUploadResults={() =>
          openDocumentUpload({
            defaultCategory: "medical_records",
          })
        }
        onOpenCycle={(cycleLink) => {
          if (cycleLink.isPast && cycleLink.cycleId) {
            navigation.navigate("PastCycleDetail", {
              cycleId: cycleLink.cycleId,
            });
            return;
          }
          navigation.navigate("CycleDetail", {
            cycleId: cycleLink.cycleId ?? undefined,
          });
        }}
        onOpenTest={(testId) => {
          navigation.getParent()?.dispatch(
            CommonActions.navigate({
              name: "Access",
              params: {
                screen: "TestDetail",
                params: { testId },
              },
            }),
          );
        }}
        onClose={handleBack}
      />
    </View>
  );
}
